import { cn } from "@/lib/utils";
import type { Severity } from "@/components/finding-badges";

export type SeverityTone = "critical" | "important" | "minor";

/**
 * Map a finding severity to its editorial dot tone. Some surfaces (e.g.
 * scenario-detail) factor in additional inputs like taskImpact and define
 * their own mapping — those keep a local helper. Use this when severity
 * alone is the signal.
 */
export function severityTone(
  severity: Severity | null | undefined,
): SeverityTone {
  if (severity === "critical") return "critical";
  if (severity === "major") return "important";
  return "minor";
}

interface SeverityDotProps {
  tone: SeverityTone;
  className?: string;
}

/**
 * Editorial severity marker — a small filled circle (5px for minor, 7px
 * for everything else) that anchors a finding row without the visual
 * weight of a badge. Intentionally aria-hidden; the surrounding text
 * carries the meaning for assistive tech.
 */
export function SeverityDot({ tone, className }: SeverityDotProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block rounded-full",
        tone === "minor" ? "h-[5px] w-[5px]" : "h-[7px] w-[7px]",
        tone === "critical" && "bg-mark-critical",
        tone === "important" && "bg-mark-important",
        tone === "minor" && "bg-mark-minor",
        className,
      )}
    />
  );
}
